import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import SellConfigBand from '../components/proposalGrid/SellConfigBand';
import { gridStyle, fmtUSD, fmtUSDc, fmtQty } from '../components/proposalGrid/helpers';

// PRD-024 — buyer's read-only public mirror of the worksheet. Per-Outcome
// pricing on the SAME grid (Sell unit | Unit of measure | Price | Qty | Total
// price); cost/margin never render (seller-only — Principle #6, FR-12). Buyer
// terminology: "Total price" per line, "Total contract price" header — never
// "Revenue". This is the unauthenticated share-link surface; the full 5-block
// spec table renders for the authenticated buyer (ProposalDetail, myRole=buyer),
// which has the spec data source. Here we show the deliverable summaries.

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const statusColors = {
  draft: 'bg-gray-100 text-gray-700',
  proposed: 'bg-blue-100 text-blue-700',
  negotiating: 'bg-yellow-100 text-yellow-700',
  signed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-700',
};

export default function PublicProposal() {
  const { shareToken } = useParams();
  const [proposal, setProposal] = useState(null);
  const [outcomes, setOutcomes] = useState([]);
  const [agreementProgress, setAgreementProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);

  useEffect(() => { fetchProposal(); }, [shareToken]);

  const fetchProposal = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/public/proposal/${shareToken}`);
      setProposal(response.data.proposal);
      setOutcomes(response.data.outcomes);
      setAgreementProgress(response.data.agreementProgress);
    } catch (err) {
      setError(err.response?.status === 404 ? 'Proposal not found' : 'Failed to load proposal');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!window.confirm('Are you sure you want to accept this proposal?')) return;
    setAccepting(true);
    try {
      await axios.post(`${API_BASE_URL}/api/public/proposal/${shareToken}/accept`);
      await fetchProposal();
      alert('Proposal accepted successfully!');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to accept proposal');
    } finally {
      setAccepting(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Oops!</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const canAccept = ['proposed', 'negotiating'].includes(proposal.status);

  // PRD-024 — per-Outcome pricing; the total is the SUM of the children
  // (price × qty), not the retired Proposal-level Sell Config.
  const rows = outcomes.map((o) => {
    const price = parseFloat(o.sellPricePerUnit) || 0;
    const qty = parseFloat(o.sellQuantity) || 0;
    return {
      ...o,
      pricePerUnitCents: Math.round(price * 100),
      qty,
      revenueCents: Math.round(price * qty * 100),
    };
  });
  const totalValueCents = rows.reduce((a, r) => a + r.revenueCents, 0);
  const distinctUnitCount = new Set(
    outcomes.map((o) => o.sellUnitOfMeasure).filter(Boolean)
  ).size;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 py-6 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">{proposal.providerName}</p>
            <h1 className="text-2xl font-bold text-gray-900">{proposal.name}</h1>
            <p className="text-xs text-gray-600 mt-1">Prepared for {proposal.customerName}{proposal.customerCompany ? ` · ${proposal.customerCompany}` : ''}</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[proposal.status]}`}>{proposal.status}</span>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-4">
        {proposal.description && (
          <div className="card p-5">
            <h2 className="text-sm font-semibold text-gray-900 mb-1">Overview</h2>
            <p className="text-gray-600 text-sm">{proposal.description}</p>
          </div>
        )}

        {/* Derived hero strip — buyer mode (Total contract price · outcomes). */}
        <SellConfigBand
          agreementValueCents={totalValueCents}
          outcomeCount={outcomes.length}
          distinctUnitCount={distinctUnitCount}
          showCost={false}
          isBuyer
          stage="proposal"
        />

        {/* Read-only buyer worksheet — per-Outcome pricing; cost/margin hidden. */}
        <div className="card overflow-hidden">
          <div style={gridStyle(false)} className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-[10px] font-bold uppercase tracking-wide text-gray-600">
            <div>Sell unit</div>
            <div>Unit of measure</div>
            <div className="text-right">Price</div>
            <div className="text-right">Qty</div>
            <div className="text-right">Total price</div>
          </div>

          {rows.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-gray-600">No outcomes defined.</div>
          ) : rows.map((o) => (
            <div key={o.id} className="border-b border-gray-100 last:border-b-0">
              <div className="px-4 pt-3 pb-1 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-primary-500 shrink-0" />
                <span className="font-semibold text-gray-900 truncate">{o.name}</span>
              </div>
              <div style={gridStyle(false)} className="px-4 pb-3 items-center">
                <div className="text-sm text-gray-700 truncate" title={o.sellUnitName || ''}>{o.sellUnitName || '—'}</div>
                <div className="text-sm text-gray-600 truncate">{o.sellUnitOfMeasure || '—'}</div>
                <div className="text-right text-sm text-gray-700 tabular-nums">{o.pricePerUnitCents ? fmtUSDc(o.pricePerUnitCents) : '—'}</div>
                <div className="text-right text-sm text-gray-700 tabular-nums">{o.qty ? fmtQty(o.qty) : '—'}</div>
                <div className="text-right font-bold text-gray-900 tabular-nums text-[15px]">{fmtUSD(o.revenueCents)}</div>
              </div>
              {/* Deliverables (read-only) */}
              {o.deliverables?.length > 0 && (
                <div className="px-4 pb-3">
                  {o.deliverables.map((d) => (
                    <div key={d.id} className="text-xs text-gray-600 pl-1 py-0.5">
                      <span className="font-medium text-gray-700">{d.name}</span>
                      {d.measureOfDelivery ? ` · ${d.measureOfDelivery}` : ''}
                      {d.dueDate ? ` · due ${formatDate(d.dueDate)}` : ''}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Footer rollup — Total contract price = Σ children. */}
          <div style={gridStyle(false)} className="px-4 py-3 bg-gray-50 border-t-2 border-gray-200 font-bold text-gray-900 items-center">
            <div style={{ gridColumn: '1 / 5' }}>Total contract price <span className="text-gray-600 font-semibold text-xs ml-1">· {rows.length} outcome{rows.length === 1 ? '' : 's'}</span></div>
            <div className="text-right tabular-nums">{fmtUSDc(totalValueCents)}</div>
          </div>
        </div>

        {/* Scope notes */}
        {proposal.scopeNotes && (
          <div className="card p-5 bg-yellow-50 border-yellow-200">
            <h2 className="text-sm font-semibold text-yellow-800 mb-1">Scope Notes</h2>
            <p className="text-yellow-700 text-sm">{proposal.scopeNotes}</p>
          </div>
        )}

        {/* Progress + accept */}
        {agreementProgress && agreementProgress.percent > 0 && (
          <div className="card p-5">
            <h3 className="font-medium text-gray-900 mb-2 text-sm">Progress</h3>
            <div className="flex justify-between text-xs text-gray-600 mb-1">
              <span>{agreementProgress.completedDeliverables} of {agreementProgress.totalDeliverables} deliverables</span>
              <span>{agreementProgress.percent}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-primary-600 h-2 rounded-full" style={{ width: `${agreementProgress.percent}%` }} />
            </div>
          </div>
        )}

        {canAccept && (
          <button onClick={handleAccept} disabled={accepting} className="btn-primary w-full py-3 text-lg">
            {accepting ? 'Processing...' : 'Accept Proposal'}
          </button>
        )}
        {proposal.status === 'signed' && (
          <div className="card p-5 bg-emerald-50 border-emerald-200 text-center">
            <p className="font-medium text-emerald-800">Proposal Signed</p>
            <p className="text-sm text-emerald-600 mt-1">This proposal has been accepted and is now an active agreement.</p>
          </div>
        )}
      </div>

      <div className="bg-white border-t border-gray-200 mt-8">
        <div className="max-w-5xl mx-auto px-4 py-6 text-center text-sm text-gray-600">Powered by Verdict</div>
      </div>
    </div>
  );
}
